/**
 * Skaper-samarbeid – kalender og bytte-system mellom skapere.
 * Tre tabs: Kalender · Oppslagstavle · Pakker
 */
import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar as CalIcon, Package, Megaphone, ArrowLeft, Link as LinkIcon, Search, Users, HelpCircle, Inbox, Handshake } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";

import CollabCalendar from "../components/collab/CollabCalendar";
import CollabBoard from "../components/collab/CollabBoard";
import CollabPackets from "../components/collab/CollabPackets";
import CollabRequestModal from "../components/collab/CollabRequestModal";
import CollabInvitations from "../components/collab/CollabInvitations";
import CollabFind from "../components/collab/CollabFind";
import CollabAccounts from "../components/collab/CollabAccounts";
import CollabExternalInbox from "../components/collab/CollabExternalInbox";
import CollabOpenRequestsInbox from "../components/collab/CollabOpenRequestsInbox";

import CreatorTour from "../components/CreatorTour";
import { CALENDAR_TOUR_STEPS } from "../components/tour/steps_calendar";

export default function Samarbeid() {
  const { user, loading, refresh } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "calendar";
  const setTab = (k) => {
    const np = new URLSearchParams(searchParams);
    np.set("tab", k);
    setSearchParams(np, { replace: true });
  };
  const requestId = searchParams.get("request");
  const [showInvite, setShowInvite] = useState(false);
  const [showCalTour, setShowCalTour] = useState(false);

  // Auto-vis kalender-tour første gang ELLER hvis ?tour=1 i URL
  useEffect(() => {
    if (!user || user === false) return;
    if (!user.is_creator && !["admin", "owner"].includes(user.role)) return;
    const force = searchParams.get("tour") === "1";
    const firstTime = user.calendar_tour_completed !== true && user.samarbeid_tour_completed !== true;
    if (force || firstTime) {
      const tm = setTimeout(() => setShowCalTour(true), 500);
      return () => clearTimeout(tm);
    }
  }, [user, searchParams]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_creator && !["admin", "owner"].includes(user.role)) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-testid="not-creator-page">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-4">
            {t("collab.creator_only_title")}
          </h1>
          <p className="text-[var(--cream-soft)] mb-6">{t("collab.creator_only_body")}</p>
          <Link to="/bli-skaper" className="btn-magenta">{t("collab.apply_creator")}</Link>
        </div>
      </Layout>
    );
  }

  const TABS = [
    { k: "calendar", label: t("collab.tab_calendar"), icon: CalIcon, testid: "tab-calendar" },
    { k: "find",     label: t("collab.tab_find"),     icon: Search, testid: "tab-find" },
    { k: "board",    label: t("collab.tab_available"),    icon: Megaphone, testid: "tab-board" },
    { k: "packets",  label: t("collab.tab_packets"),  icon: Package, testid: "tab-packets" },
    { k: "accounts", label: t("collab.tab_accounts"), icon: Users, testid: "tab-accounts" },
    { k: "open-inbox", label: "Åpne forespørsler", icon: Handshake, testid: "tab-open-inbox" },
    { k: "external-inbox", label: t("collab.tab_external_inbox", "Ekstern innboks"), icon: Inbox, testid: "tab-external-inbox" },
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="samarbeid-page">
        <div className="mb-6 flex items-center gap-3">
          <Link to="/skaper/studio" className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
            data-testid="samarbeid-back">
            <ArrowLeft className="h-4 w-4" /> {t("studio.back_to_page")}
          </Link>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">
            {t("collab.title")}
          </h1>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowCalTour(true)}
              className="px-4 py-2 border border-[var(--magenta)]/60 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 font-meta inline-flex items-center gap-2"
              data-testid="samarbeid-tour-btn"
              title="Vis omvisning"
            >
              <HelpCircle className="h-4 w-4" /> {t("calendar_tour.show_again", { defaultValue: "Omvisning" })}
            </button>
            <button
              onClick={() => setShowInvite(true)}
              className="px-4 py-2 border border-[var(--gold)]/40 text-[var(--gold)] font-meta inline-flex items-center gap-2"
              data-testid="invite-btn"
            >
              <LinkIcon className="h-4 w-4" /> {t("collab.invite_btn")}
            </button>
          </div>
        </div>
        <p className="text-sm text-[var(--cream-soft)] mb-8 max-w-2xl">
          {t("collab.subtitle")}
        </p>

        <nav className="border-y border-white/10 mb-8">
          <div className="flex gap-2 overflow-x-auto">
            {TABS.map(({ k, label, icon: Icon, testid }) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={testid}
                className={`flex items-center gap-2 px-4 py-3 font-meta transition-colors whitespace-nowrap ${
                  tab === k
                    ? "text-[var(--magenta)] border-b-2 border-[var(--magenta)]"
                    : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </nav>

        {tab === "calendar" && <CollabCalendar />}
        {tab === "find" && <CollabFind />}
        {tab === "board" && <CollabBoard />}
        {tab === "packets" && <CollabPackets />}
        {tab === "accounts" && <CollabAccounts />}
        {tab === "open-inbox" && <CollabOpenRequestsInbox />}
        {tab === "external-inbox" && <CollabExternalInbox />}
      </div>

      {requestId && <CollabRequestModal requestId={requestId} onClose={() => setSearchParams({})} />}
      {showInvite && <CollabInvitations onClose={() => setShowInvite(false)} />}
      {showCalTour && (
        <CreatorTour
          onClose={() => setShowCalTour(false)}
          onComplete={() => { refresh?.(); }}
          onStepChange={(step) => {
            if (step?.tab && step.tab !== tab) setTab(step.tab);
          }}
          completeEndpoint="/me/calendar-tour-completed"
          steps={CALENDAR_TOUR_STEPS.map((s) => ({
            ...s,
            title: t(s.titleKey),
            body: t(s.bodyKey),
          }))}
        />
      )}
    </Layout>
  );
}
